import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminApi } from "@/api/adminOneClient";
import ComplianceLock from "@/components/compliance/ComplianceLock";

/**
 * CareLogix™ ORACLE Intelligence Dashboard™ — AdminOne Command Surface (Phase 25).
 *
 * Strictly additive. AdminOne-only. JWT-protected by upstream <AdminProtectedRoute>.
 * Aggregates LOGIX-2™ + NiSi™ outputs across all asset classes into one premium
 * command surface — no dead tabs, every section pulls from a real backend endpoint.
 *
 * Tabs (10 widgets per spec):
 *   1. Top Opportunities          — cross-asset ranked by opportunity_score
 *   2. Highest Confidence Trades  — top confidence_score, mostly stocks/FX/crypto
 *   3. Market Heatmap             — sector breadth + flow grid
 *   4. Forex Strength Matrix      — 8x8 currency strength
 *   5. Crypto Momentum            — crypto by momentum
 *   6. Sports Opportunities       — 21+ where legal, edge_pct + win prob
 *   7. Healthcare Performance     — XLV/IBB/XBI/IHE/IHI
 *   8. African Markets            — NGX/JSE/EGX/NSE/GSE/MASI
 *   9. Global AI Tracker          — NVDA/MSFT/AMD/META/GOOGL/ORCL/SMCI/ARM
 *  10. Biotech Catalysts          — upcoming FDA/Phase 3 events
 */

const TABS = [
  { id: "top",        label: "TOP OPPORTUNITIES",  icon: "★" },
  { id: "highconf",   label: "HIGHEST CONFIDENCE", icon: "✓" },
  { id: "heatmap",    label: "MARKET HEATMAP",     icon: "▦" },
  { id: "fxmatrix",   label: "FX STRENGTH",        icon: "⇄" },
  { id: "crypto",     label: "CRYPTO MOMENTUM",    icon: "₿" },
  { id: "sports",     label: "SPORTS",             icon: "⚽" },
  { id: "healthcare", label: "HEALTHCARE",         icon: "✚" },
  { id: "africa",     label: "AFRICAN MARKETS",    icon: "🌍" },
  { id: "ai",         label: "GLOBAL AI",          icon: "🤖" },
  { id: "biotech",    label: "BIOTECH CALENDAR",   icon: "🧬" },
];

const DIRECTION_COLOR = {
  bullish: "#10b981",
  bearish: "#ef4444",
  neutral: "#c5a059",
};

function scorePill(value, kind = "confidence") {
  const v = Math.max(0, Math.min(100, Number(value || 0)));
  const palette = {
    confidence:  { bg: "rgba(16,185,129,0.10)",  bd: "rgba(16,185,129,0.35)", fg: "#10b981" },
    risk:        { bg: "rgba(239,68,68,0.10)",   bd: "rgba(239,68,68,0.35)",  fg: "#fca5a5" },
    opportunity: { bg: "rgba(197,160,89,0.12)",  bd: "rgba(197,160,89,0.40)", fg: "#ffd966" },
  }[kind] || { bg: "rgba(255,255,255,0.06)", bd: "rgba(255,255,255,0.18)", fg: "#cbd5e1" };
  return { ...S.scorePill, background: palette.bg, borderColor: palette.bd, color: palette.fg, content: v };
}

function ScoreBar({ value, kind = "confidence" }) {
  const v = Math.max(0, Math.min(100, Number(value || 0)));
  const palette = {
    confidence:  "linear-gradient(90deg, #34d399, #10b981)",
    risk:        "linear-gradient(90deg, #fda4af, #ef4444)",
    opportunity: "linear-gradient(90deg, #ffd966, #c5a059)",
  }[kind] || "linear-gradient(90deg, #94a3b8, #475569)";
  return (
    <div style={S.bar}>
      <div style={{ ...S.barFill, width: `${v}%`, background: palette }} />
    </div>
  );
}

function DirPill({ direction }) {
  const d = (direction || "neutral").toLowerCase();
  const color = DIRECTION_COLOR[d] || DIRECTION_COLOR.neutral;
  return (
    <span style={{ ...S.dirPill, color, borderColor: color, background: `${color}1a` }}>
      {d.toUpperCase()}
    </span>
  );
}

function OpportunityCard({ item, testid }) {
  const sym = item.symbol;
  const label = item.name || item.market || item.event || sym;
  return (
    <div style={S.card} data-testid={testid}>
      <div style={S.cardHead}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <code style={S.symCode}>{sym}</code>
          <strong style={S.cardTitle} title={label}>{label}</strong>
        </div>
        <DirPill direction={item.direction} />
      </div>

      <div style={S.scoreRow}>
        <div style={S.scoreCell}>
          <span style={S.scoreLabel}>OPPORTUNITY</span>
          <span style={{ ...S.scoreVal, color: "#c5a059" }}>{item.opportunity_score}%</span>
          <ScoreBar value={item.opportunity_score} kind="opportunity" />
        </div>
        <div style={S.scoreCell}>
          <span style={S.scoreLabel}>CONFIDENCE</span>
          <span style={{ ...S.scoreVal, color: "#10b981" }}>{item.confidence_score}%</span>
          <ScoreBar value={item.confidence_score} kind="confidence" />
        </div>
        <div style={S.scoreCell}>
          <span style={S.scoreLabel}>RISK</span>
          <span style={{ ...S.scoreVal, color: "#fca5a5" }}>{item.risk_score}%</span>
          <ScoreBar value={item.risk_score} kind="risk" />
        </div>
      </div>

      <div style={S.metaRow}>
        <span style={S.metaLabel}>ASSET</span>
        <code style={S.metaVal}>{item.asset_class}</code>
      </div>
      <div style={S.metaRow}>
        <span style={S.metaLabel}>EXPECTED RANGE</span>
        <code style={S.metaVal}>
          {item.expected_range?.low} → {item.expected_range?.high}
        </code>
      </div>
      <div style={S.metaRow}>
        <span style={S.metaLabel}>24H</span>
        <code style={{ ...S.metaVal, color: (item.pct_change_24h ?? 0) >= 0 ? "#10b981" : "#ef4444" }}>
          {item.pct_change_24h >= 0 ? "+" : ""}{item.pct_change_24h}%
        </code>
      </div>
      {item.probability_model && (
        <div style={S.probRow}>
          <span style={S.probLabel}>CONT</span><code style={S.probVal}>{item.probability_model.trend_continuation}%</code>
          <span style={S.probLabel}>REV</span><code style={S.probVal}>{item.probability_model.reversal}%</code>
          <span style={S.probLabel}>RANGE</span><code style={S.probVal}>{item.probability_model.range_bound}%</code>
          <span style={S.probLabel}>VOL</span><code style={S.probVal}>{item.probability_model.volatility_spike}%</code>
        </div>
      )}
      {/* Phase 26C — Inline ComplianceLock™ on every signal card */}
      <ComplianceLock variant="inline" short testid={`${testid}-compliance`} />
    </div>
  );
}

function Heatmap({ data }) {
  if (!data?.cells) return <p style={S.empty}>Loading heatmap…</p>;
  const max = Math.max(...data.cells.map((c) => Math.abs(c.change || 0)), 0.01);
  return (
    <div style={S.heatGrid} data-testid="intel-heatmap-grid">
      {data.cells.map((c) => {
        const intensity = Math.min(1, Math.abs(c.change) / max);
        const color = c.change >= 0 ? "16,185,129" : "239,68,68";
        const bg = `rgba(${color}, ${0.10 + intensity * 0.40})`;
        return (
          <div key={c.sector} style={{ ...S.heatCell, background: bg, borderColor: `rgba(${color}, 0.45)` }}>
            <strong style={S.heatSector}>{c.sector}</strong>
            <span style={{ ...S.heatChange, color: c.change >= 0 ? "#10b981" : "#ef4444" }}>
              {c.change >= 0 ? "+" : ""}{c.change}%
            </span>
            <div style={S.heatMetaRow}>
              <span>Breadth {Math.round((c.breadth || 0) * 100)}%</span>
              <span>Flow {c.flow_score}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FxMatrix({ data }) {
  if (!data?.matrix) return <p style={S.empty}>Loading FX strength matrix…</p>;
  return (
    <div style={S.matrixWrap} data-testid="intel-fx-matrix">
      <table style={S.matrixTable}>
        <thead>
          <tr>
            <th style={S.matrixCorner}>BASE \ QUOTE</th>
            {data.currencies.map((c) => <th key={`h-${c}`} style={S.matrixHead}>{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {data.matrix.map((row, ri) => (
            <tr key={`r-${ri}`}>
              <th style={S.matrixRowHead}>{data.currencies[ri]}</th>
              {row.map((cell, ci) => {
                if (cell === null) return <td key={`c-${ri}-${ci}`} style={{ ...S.matrixCell, background: "rgba(255,255,255,0.04)" }}>—</td>;
                const intensity = Math.min(1, Math.abs(cell) / 70);
                const color = cell >= 0 ? "16,185,129" : "239,68,68";
                return (
                  <td
                    key={`c-${ri}-${ci}`}
                    style={{
                      ...S.matrixCell,
                      background: `rgba(${color}, ${0.10 + intensity * 0.40})`,
                      color: cell >= 0 ? "#10b981" : "#ef4444",
                    }}
                  >{cell >= 0 ? "+" : ""}{cell}</td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p style={S.captionSmall}>{data.caption}</p>
    </div>
  );
}

export default function AdminOracleIntelligenceDashboard() {
  const [tab, setTab] = useState("top");
  const [dashboard, setDashboard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await adminApi.intelligenceDashboard();
      setDashboard(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "load_failed");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => ({
    top:        dashboard?.top_opportunities?.length || 0,
    highconf:   dashboard?.highest_confidence?.length || 0,
    crypto:     dashboard?.crypto_momentum?.length || 0,
    sports:     dashboard?.sports_opportunities?.length || 0,
    healthcare: dashboard?.healthcare_performance?.length || 0,
    africa:     dashboard?.african_performance?.length || 0,
    ai:         dashboard?.global_ai_tracker?.length || 0,
    biotech:    dashboard?.biotech_catalysts?.length || 0,
  }), [dashboard]);

  return (
    <div style={S.canvas} data-testid="intel-dashboard-page">
      {/* Phase 26C — Mandated Compliance Lock™ banner (research only / HITL) */}
      <ComplianceLock variant="banner" testid="intel-dashboard-compliance-top" />

      <div style={S.banner} data-testid="intel-dashboard-banner">
        <span style={{ color: "#10b981" }}>●</span>
        <span>
          <strong>ORACLE Intelligence Dashboard™</strong> · LOGIX-2™ + NiSi™ ·
          Predictive Intelligence · Risk Scoring · Cross-Asset Opportunity Detection ·
          Research only · Not financial advice
        </span>
      </div>

      <header style={S.header}>
        <div>
          <h1 style={S.title}>ORACLE Intelligence Layer™</h1>
          <p style={S.subtitle}>
            Market Data → Intelligence → Prediction → Action. Powered by{" "}
            <b style={{ color: "#c5a059" }}>LOGIX-2™</b> +{" "}
            <b style={{ color: "#10b981" }}>NiSi™</b> across 12 asset classes.
          </p>
        </div>
        <div style={S.headerActions}>
          <span style={S.livePill} data-testid="intel-live-pill">LIVE · 24/7</span>
          <button
            type="button"
            onClick={load}
            disabled={busy}
            style={S.refreshBtn}
            data-testid="intel-refresh"
          >{busy ? "…" : "↻ Refresh Intelligence"}</button>
        </div>
      </header>

      {err && <div style={S.errBanner} data-testid="intel-error">⚠ {err}</div>}

      <nav style={S.tabs} role="tablist" aria-label="Intelligence Dashboard tabs">
        {TABS.map((t) => {
          const cnt = counts[t.id];
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              data-testid={`intel-tab-${t.id}`}
              style={{
                ...S.tab,
                color: tab === t.id ? "#c5a059" : "#94a3b8",
                borderBottomColor: tab === t.id ? "#c5a059" : "transparent",
              }}
            >
              <span style={{ marginRight: 6 }}>{t.icon}</span>
              {t.label}
              {cnt ? <span style={S.tabCount}>{cnt}</span> : null}
            </button>
          );
        })}
      </nav>

      <main style={S.main}>
        {!dashboard && !err && <p style={S.empty}>Loading LOGIX-2™ + NiSi™ intelligence…</p>}

        {/* 1. TOP OPPORTUNITIES */}
        {tab === "top" && dashboard && (
          <section style={S.panel} data-testid="intel-section-top">
            <h2 style={S.heading}>Top Opportunities Today
              <span style={S.countChip}>{counts.top} candidates · ranked by Opportunity Score</span>
            </h2>
            <div style={S.grid}>
              {dashboard.top_opportunities.map((it, i) => (
                <OpportunityCard key={`top-${i}`} item={it} testid={`intel-top-${i}`} />
              ))}
            </div>
          </section>
        )}

        {/* 2. HIGHEST CONFIDENCE */}
        {tab === "highconf" && dashboard && (
          <section style={S.panel} data-testid="intel-section-highconf">
            <h2 style={S.heading}>Highest Confidence Trades
              <span style={S.countChip}>{counts.highconf} · ranked by Confidence Score</span>
            </h2>
            <div style={S.grid}>
              {dashboard.highest_confidence.map((it, i) => (
                <OpportunityCard key={`hc-${i}`} item={it} testid={`intel-highconf-${i}`} />
              ))}
            </div>
          </section>
        )}

        {/* 3. MARKET HEATMAP */}
        {tab === "heatmap" && dashboard && (
          <section style={S.panel} data-testid="intel-section-heatmap">
            <h2 style={S.heading}>Market Heatmap
              <span style={S.countChip}>{dashboard.market_heatmap.cells.length} sectors</span>
            </h2>
            <Heatmap data={dashboard.market_heatmap} />
          </section>
        )}

        {/* 4. FX STRENGTH MATRIX */}
        {tab === "fxmatrix" && dashboard && (
          <section style={S.panel} data-testid="intel-section-fxmatrix">
            <h2 style={S.heading}>Forex Strength Matrix
              <span style={S.countChip}>{dashboard.forex_strength_matrix.currencies.length} currencies</span>
            </h2>
            <FxMatrix data={dashboard.forex_strength_matrix} />
          </section>
        )}

        {/* 5. CRYPTO MOMENTUM */}
        {tab === "crypto" && dashboard && (
          <section style={S.panel} data-testid="intel-section-crypto">
            <h2 style={S.heading}>Crypto Momentum Rankings
              <span style={S.countChip}>{counts.crypto} pairs</span>
            </h2>
            <div style={S.grid}>
              {dashboard.crypto_momentum.map((it, i) => (
                <OpportunityCard key={`cr-${i}`} item={it} testid={`intel-crypto-${i}`} />
              ))}
            </div>
          </section>
        )}

        {/* 6. SPORTS */}
        {tab === "sports" && dashboard && (
          <section style={S.panel} data-testid="intel-section-sports">
            <h2 style={S.heading}>Sports Betting Opportunities
              <span style={S.countChip}>{counts.sports} events · 21+ where legal</span>
            </h2>
            <div style={S.grid}>
              {dashboard.sports_opportunities.map((it, i) => (
                <OpportunityCard key={`sp-${i}`} item={it} testid={`intel-sports-${i}`} />
              ))}
            </div>
            <p style={S.captionSmall}>
              Sports intelligence is research output. Bet only what you can afford to lose.
              Age restriction: 21+ where legal.
            </p>
          </section>
        )}

        {/* 7. HEALTHCARE */}
        {tab === "healthcare" && dashboard && (
          <section style={S.panel} data-testid="intel-section-healthcare">
            <h2 style={S.heading}>Healthcare Sector Performance
              <span style={S.countChip}>{counts.healthcare} indexes</span>
            </h2>
            <div style={S.grid}>
              {dashboard.healthcare_performance.map((it, i) => (
                <OpportunityCard key={`hc-${i}`} item={it} testid={`intel-healthcare-${i}`} />
              ))}
            </div>
          </section>
        )}

        {/* 8. AFRICA */}
        {tab === "africa" && dashboard && (
          <section style={S.panel} data-testid="intel-section-africa">
            <h2 style={S.heading}>African Market Performance
              <span style={S.countChip}>{counts.africa} markets</span>
            </h2>
            <div style={S.grid}>
              {dashboard.african_performance.map((it, i) => (
                <OpportunityCard key={`af-${i}`} item={it} testid={`intel-africa-${i}`} />
              ))}
            </div>
          </section>
        )}

        {/* 9. GLOBAL AI */}
        {tab === "ai" && dashboard && (
          <section style={S.panel} data-testid="intel-section-ai">
            <h2 style={S.heading}>Global AI Sector Tracker
              <span style={S.countChip}>{counts.ai} tickers</span>
            </h2>
            <div style={S.grid}>
              {dashboard.global_ai_tracker.map((it, i) => (
                <OpportunityCard key={`ai-${i}`} item={it} testid={`intel-ai-${i}`} />
              ))}
            </div>
          </section>
        )}

        {/* 10. BIOTECH */}
        {tab === "biotech" && dashboard && (
          <section style={S.panel} data-testid="intel-section-biotech">
            <h2 style={S.heading}>Biotech Catalyst Calendar
              <span style={S.countChip}>{counts.biotech} upcoming events</span>
            </h2>
            <div style={S.grid}>
              {dashboard.biotech_catalysts.map((it, i) => (
                <div key={`bt-${i}`} style={S.card} data-testid={`intel-biotech-${i}`}>
                  <div style={S.cardHead}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <code style={S.symCode}>{it.symbol}</code>
                      <strong style={S.cardTitle}>{it.name}</strong>
                    </div>
                    <DirPill direction={it.direction} />
                  </div>
                  <p style={S.bodySmall}>{it.event}</p>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>EXPECTED</span>
                    <code style={S.metaVal}>{it.expected_date} ({it.days_to_event}d)</code>
                  </div>
                  <div style={S.scoreRow}>
                    <div style={S.scoreCell}>
                      <span style={S.scoreLabel}>OPPORTUNITY</span>
                      <span style={{ ...S.scoreVal, color: "#c5a059" }}>{it.opportunity_score}%</span>
                      <ScoreBar value={it.opportunity_score} kind="opportunity" />
                    </div>
                    <div style={S.scoreCell}>
                      <span style={S.scoreLabel}>CONFIDENCE</span>
                      <span style={{ ...S.scoreVal, color: "#10b981" }}>{it.confidence_score}%</span>
                      <ScoreBar value={it.confidence_score} kind="confidence" />
                    </div>
                    <div style={S.scoreCell}>
                      <span style={S.scoreLabel}>RISK</span>
                      <span style={{ ...S.scoreVal, color: "#fca5a5" }}>{it.risk_score}%</span>
                      <ScoreBar value={it.risk_score} kind="risk" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      {dashboard?.disclaimer && (
        <footer style={S.discl} data-testid="intel-disclaimer">{dashboard.disclaimer}</footer>
      )}
    </div>
  );
}

const S = {
  canvas: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at top left, rgba(16,185,129,0.10), #040608 30%, #020304 100%)",
    color: "#f8fafc",
    fontFamily: "'Figtree', 'Inter', system-ui, sans-serif",
  },
  banner: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "9px 22px",
    background: "linear-gradient(90deg, rgba(16,185,129,0.10), rgba(197,160,89,0.06))",
    borderBottom: "1px solid rgba(16,185,129,0.18)",
    fontFamily: "'Space Mono', monospace",
    fontSize: 11.5, letterSpacing: "0.04em", color: "#d1fae5",
  },
  header: {
    padding: "20px 26px 16px",
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    gap: 14, flexWrap: "wrap",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
  },
  title: { color: "#c5a059", fontSize: 24, margin: 0, fontWeight: 800 },
  subtitle: { color: "#94a3b8", fontSize: 13, maxWidth: 820, marginTop: 6 },
  headerActions: { display: "flex", gap: 8, alignItems: "center" },
  livePill: {
    color: "#10b981",
    border: "1px solid rgba(16,185,129,0.35)",
    background: "rgba(16,185,129,0.10)",
    padding: "6px 11px", borderRadius: 999,
    fontSize: 10.5, fontWeight: 900, fontFamily: "monospace", letterSpacing: "0.10em",
  },
  refreshBtn: {
    appearance: "none",
    background: "linear-gradient(135deg, #c5a059, #ffd966)",
    border: "1px solid #c5a059", color: "#0c1a10",
    padding: "6px 12px", borderRadius: 8, cursor: "pointer",
    fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.10em",
  },
  errBanner: {
    margin: "10px 26px 0",
    padding: "8px 12px",
    background: "rgba(239,68,68,0.10)",
    border: "1px solid rgba(239,68,68,0.30)",
    color: "#fecaca", borderRadius: 10, fontSize: 12.5,
  },
  tabs: {
    display: "flex", gap: 18, padding: "0 26px",
    background: "rgba(11,15,20,0.92)",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    overflowX: "auto",
  },
  tab: {
    background: "transparent", border: "none",
    borderBottom: "2px solid",
    padding: "14px 0", fontSize: 11.5, fontWeight: 900,
    cursor: "pointer", letterSpacing: "0.08em",
    fontFamily: "'Space Mono', monospace",
    whiteSpace: "nowrap",
    display: "flex", alignItems: "center",
  },
  tabCount: {
    marginLeft: 8,
    background: "rgba(197,160,89,0.18)",
    color: "#ffd966",
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: 10,
  },
  main: { padding: "20px 26px 30px" },
  panel: {
    background: "rgba(11,15,20,0.94)",
    border: "1px solid rgba(197,160,89,0.14)",
    borderRadius: 16,
    padding: 22,
  },
  heading: {
    color: "#e2e8f0",
    marginTop: 0,
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
    fontSize: 16, flexWrap: "wrap",
  },
  countChip: {
    fontSize: 11, fontFamily: "'Space Mono', monospace", letterSpacing: "0.08em",
    background: "rgba(197,160,89,0.10)",
    color: "#c5a059",
    border: "1px solid rgba(197,160,89,0.28)",
    padding: "3px 9px",
    borderRadius: 999,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))",
    gap: 12,
    marginTop: 12,
  },
  card: {
    background: "#040608",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 12,
    padding: 14,
    display: "flex", flexDirection: "column", gap: 8,
  },
  cardHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  cardTitle: { color: "#e2e8f0", fontSize: 13.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  symCode: {
    background: "rgba(197,160,89,0.12)",
    color: "#c5a059",
    padding: "2px 8px",
    borderRadius: 6,
    fontFamily: "'Space Mono', monospace",
    fontSize: 11.5, fontWeight: 800,
  },
  dirPill: {
    fontSize: 10, fontFamily: "'Space Mono', monospace", letterSpacing: "0.12em",
    fontWeight: 800,
    padding: "3px 8px",
    border: "1px solid",
    borderRadius: 999,
  },
  scoreRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 8,
    marginTop: 4,
  },
  scoreCell: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.05)",
    borderRadius: 8,
    padding: "8px 10px",
    display: "flex", flexDirection: "column", gap: 4,
  },
  scoreLabel: {
    fontFamily: "'Space Mono', monospace",
    fontSize: 9.5, letterSpacing: "0.10em",
    color: "#64748b",
  },
  scoreVal: { fontSize: 16, fontWeight: 800, fontFamily: "'Figtree', sans-serif" },
  bar: { height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 4, overflow: "hidden" },
  barFill: { height: "100%", borderRadius: 4, transition: "width 280ms ease" },
  metaRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 },
  metaLabel: { color: "#64748b", fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.12em" },
  metaVal: {
    background: "rgba(255,255,255,0.04)",
    color: "#cbd5e1",
    padding: "2px 6px",
    borderRadius: 6,
    fontFamily: "'Space Mono', monospace",
    fontSize: 11,
  },
  probRow: {
    display: "grid",
    gridTemplateColumns: "auto 1fr auto 1fr auto 1fr auto 1fr",
    gap: 4,
    alignItems: "center",
    background: "rgba(0,0,0,0.30)",
    padding: "6px 8px",
    borderRadius: 8,
  },
  probLabel: {
    fontFamily: "'Space Mono', monospace",
    fontSize: 8.5, letterSpacing: "0.10em",
    color: "#64748b",
  },
  probVal: {
    fontFamily: "'Space Mono', monospace",
    fontSize: 10, color: "#c5a059",
  },
  bodySmall: { color: "#cbd5e1", fontSize: 12.5, lineHeight: 1.5, margin: 0 },
  empty: {
    gridColumn: "1 / -1",
    color: "#94a3b8",
    textAlign: "center",
    padding: 28,
    border: "1px dashed rgba(255,255,255,0.10)",
    borderRadius: 12,
    fontFamily: "'Space Mono', monospace",
    fontSize: 12.5,
  },
  scorePill: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 38, height: 22, borderRadius: 999, border: "1px solid",
    fontSize: 11, fontWeight: 800, fontFamily: "'Space Mono', monospace",
  },
  heatGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: 8,
    marginTop: 12,
  },
  heatCell: {
    border: "1px solid",
    borderRadius: 10,
    padding: 12,
    display: "flex", flexDirection: "column", gap: 4,
  },
  heatSector: { color: "#e2e8f0", fontSize: 13 },
  heatChange: { fontFamily: "'Space Mono', monospace", fontSize: 14, fontWeight: 800 },
  heatMetaRow: { display: "flex", justifyContent: "space-between", color: "#94a3b8", fontFamily: "'Space Mono', monospace", fontSize: 10, marginTop: 4 },
  matrixWrap: { marginTop: 12, overflowX: "auto" },
  matrixTable: { width: "100%", borderCollapse: "collapse", fontFamily: "'Space Mono', monospace", fontSize: 11 },
  matrixCorner: {
    background: "rgba(11,15,20,0.92)",
    color: "#64748b",
    padding: "8px 6px",
    border: "1px solid rgba(255,255,255,0.06)",
    fontSize: 9.5,
    letterSpacing: "0.08em",
  },
  matrixHead: {
    background: "rgba(197,160,89,0.10)",
    color: "#c5a059",
    padding: "8px 12px",
    border: "1px solid rgba(255,255,255,0.06)",
    textAlign: "center",
  },
  matrixRowHead: {
    background: "rgba(197,160,89,0.10)",
    color: "#c5a059",
    padding: "8px 12px",
    border: "1px solid rgba(255,255,255,0.06)",
    textAlign: "center",
  },
  matrixCell: {
    padding: "10px 12px",
    border: "1px solid rgba(255,255,255,0.06)",
    textAlign: "center",
    fontWeight: 800,
  },
  captionSmall: {
    marginTop: 10,
    color: "#94a3b8",
    fontSize: 11.5,
    fontStyle: "italic",
  },
  discl: {
    padding: "16px 26px 22px",
    color: "#64748b",
    fontSize: 11.5,
    fontStyle: "italic",
    textAlign: "center",
    borderTop: "1px solid rgba(255,255,255,0.04)",
    marginTop: 20,
  },
};
